import apiService from './apiService';

/**
 * Usage tracker — reports what the user does, by module and action.
 *
 * Call it from UI handlers, not from the API layer, so events describe user intent
 * ("Marked issue fixed") rather than HTTP verbs. Two entry points:
 *
 *   usageTracker.view('Issues')                            // a module was opened
 *   usageTracker.action('Reports', 'CREATE', 'Daily EMT')   // an action was performed
 *
 * Design rules this file holds to:
 *  - Never throw and never block. A tracking failure must not affect the page. Every
 *    public method swallows its own errors.
 *  - Batch, don't spam. Events queue and flush on a timer, so a burst of clicks costs
 *    one request rather than ten.
 *  - Flush on page hide, so the last events before a tab closes are not lost.
 *  - Never track reads/polling. The app auto-refreshes every 15-30s; recording those
 *    would bury the signal in noise.
 */

const FLUSH_INTERVAL_MS = 15000;   // batch window
const MAX_QUEUE = 100;             // hard cap; oldest dropped beyond this
const FLUSH_THRESHOLD = 20;        // flush early once the queue reaches this

class UsageTracker {
    constructor() {
        this.queue = [];
        this.timer = null;
        this.enabled = true;
        this.sessionId = null;
        this.lastView = null;       // de-dupes repeated views of the same module
        this.listenersBound = false;
    }

    /**
     * Starts the tracker. Safe to call more than once.
     * A session id groups a browser session's events; it is generated here because the
     * backend has no session of its own (auth is a bearer token per request).
     */
    start() {
        if (!this.sessionId) {
            this.sessionId = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }
        if (!this.timer) {
            this.timer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
        }
        if (!this.listenersBound && typeof document !== 'undefined') {
            // 'visibilitychange' fires reliably on tab close/switch, unlike 'unload'.
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') this.flush();
            });
            this.listenersBound = true;
        }
    }

    /** Stops batching and sends whatever is queued. */
    stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        this.flush();
    }

    /** Turn collection on/off at runtime without unwiring call sites. */
    setEnabled(enabled) {
        this.enabled = !!enabled;
        if (!this.enabled) this.queue = [];
    }

    /**
     * Record that a module/page was opened. Repeat views of the same module are ignored
     * so a re-render or a redirect back to the same page does not inflate the count.
     */
    view(module, details) {
        if (!module || this.lastView === module) return;
        this.lastView = module;
        this.push(module, 'VIEW', details);
    }

    /**
     * Record an in-page navigation — switching tabs within a module.
     *
     * Emitted as a VIEW so it counts toward "modules opened" rather than inflating the
     * actions total: moving between tabs is navigation, not work performed. De-duped on
     * module + tab so re-selecting the current tab is ignored.
     */
    tab(module, tabName) {
        if (!module || !tabName) return;
        const key = `${module}#${tabName}`;
        if (this.lastView === key) return;
        this.lastView = key;
        this.push(module, 'VIEW', `Tab: ${tabName}`);
    }

    /**
     * Record an action. `action` should be a stable verb (CREATE, UPDATE, DELETE, MAP,
     * CREATE_TICKET, …) since the report groups by it; `details` is free text for humans.
     */
    action(module, action, details) {
        if (!module || !action) return;
        // An action means the user is working in that module — let the next view of it
        // register again, so navigating away and back is counted.
        this.lastView = null;
        this.push(module, action, details);
    }

    push(module, action, details) {
        try {
            if (!this.enabled) return;
            if (!localStorage.getItem('userEmail')) return; // no identity yet, nothing to attribute

            this.queue.push({
                module: String(module).slice(0, 100),
                action: String(action).slice(0, 100),
                details: details ? String(details).slice(0, 500) : undefined,
                sessionId: this.sessionId,
                occurredAt: new Date().toISOString(),
            });

            if (this.queue.length > MAX_QUEUE) {
                this.queue = this.queue.slice(-MAX_QUEUE);
            }
            if (this.queue.length >= FLUSH_THRESHOLD) {
                this.flush();
            }
        } catch {
            // Tracking must never surface an error to the user.
        }
    }

    /**
     * Sends queued events. The queue is cleared up front so a slow request cannot cause
     * the same events to be sent twice; on failure they are dropped rather than retried,
     * because stale telemetry is not worth a retry storm.
     */
    async flush() {
        if (!this.enabled || this.queue.length === 0) return;

        const events = this.queue;
        this.queue = [];
        try {
            await apiService.trackUsage(events);
        } catch {
            // Deliberately swallowed — see above.
        }
    }
}

const usageTracker = new UsageTracker();
export default usageTracker;
